import type { SupabaseClient } from '@supabase/supabase-js';

export type OwnerType = 'eigentuemer' | 'mieter' | 'dienstleister';
export type Kanal = 'email' | 'telefon';

export interface KontaktKanal {
  id?: string;
  kanal: Kanal;
  wert: string;
  label: string | null;
  ist_primaer: boolean;
  sortierung: number;
}

export function normalisiereKanaele(kanaele: KontaktKanal[]): KontaktKanal[] {
  const gefiltert = kanaele.filter((x) => (x.wert || '').trim() !== '');
  const out: KontaktKanal[] = [];
  for (const kanal of ['email', 'telefon'] as Kanal[]) {
    const gruppe = gefiltert.filter((x) => x.kanal === kanal);
    const startIdx = out.length;
    let primaerGesetzt = false;
    gruppe.forEach((x, i) => {
      const ist_primaer = x.ist_primaer && !primaerGesetzt;
      if (ist_primaer) primaerGesetzt = true;
      out.push({ ...x, wert: x.wert.trim(), ist_primaer, sortierung: i });
    });
    if (gruppe.length > 0 && !primaerGesetzt) {
      out[startIdx] = { ...out[startIdx], ist_primaer: true };
    }
  }
  return out;
}

export function primaerWerte(kanaele: KontaktKanal[]): { email: string | null; telefon: string | null; mobil: string | null } {
  const norm = normalisiereKanaele(kanaele);
  const email = norm.find((x) => x.kanal === 'email' && x.ist_primaer)?.wert ?? null;
  const telefon = norm.find((x) => x.kanal === 'telefon' && x.ist_primaer)?.wert ?? null;
  const mobil = norm.find((x) => x.kanal === 'telefon' && (x.label || '').toLowerCase() === 'mobil')?.wert ?? null;
  return { email, telefon, mobil };
}

export async function ladeKanaele(
  supabase: SupabaseClient,
  ownerType: OwnerType,
  ownerId: string,
): Promise<KontaktKanal[]> {
  const { data, error } = await supabase
    .from('kontakt_kanaele')
    .select('id, kanal, wert, label, ist_primaer, sortierung')
    .eq('owner_type', ownerType)
    .eq('owner_id', ownerId)
    .order('kanal', { ascending: true })
    .order('sortierung', { ascending: true });
  if (error) throw new Error(`kontakt_kanaele Laden: ${error.message}`);
  return (data || []) as KontaktKanal[];
}

export async function speichereKanaele(
  supabase: SupabaseClient,
  ownerType: OwnerType,
  ownerId: string,
  kanaele: KontaktKanal[],
): Promise<void> {
  const norm = normalisiereKanaele(kanaele);
  // Replace-Strategie: alle bestehenden Kanäle des Owners ersetzen.
  const { error: delError } = await supabase.from('kontakt_kanaele').delete().eq('owner_type', ownerType).eq('owner_id', ownerId);
  if (delError) throw new Error(`kontakt_kanaele Delete: ${delError.message}`);
  if (norm.length > 0) {
    const rows = norm.map((x) => ({
      owner_type: ownerType, owner_id: ownerId,
      kanal: x.kanal, wert: x.wert, label: x.label || null,
      ist_primaer: x.ist_primaer, sortierung: x.sortierung,
    }));
    const { error } = await supabase.from('kontakt_kanaele').insert(rows);
    if (error) throw new Error(`kontakt_kanaele Insert: ${error.message}`);
  }
  // Primärwerte in die Owner-Tabelle spiegeln (Rückwärtskompatibilität).
  const { email, telefon, mobil } = primaerWerte(norm);
  const patch: Record<string, string | null> = { email, telefon };
  if (ownerType !== 'dienstleister') patch.mobil = mobil; // dienstleister hat keine mobil-Spalte
  const { error: upError } = await supabase.from(ownerType).update(patch).eq('id', ownerId);
  if (upError) throw new Error(`${ownerType} Update: ${upError.message}`);
}


/**
 * Primäre E-Mail und Telefonnummer in die bestehenden Kanäle übernehmen, ohne weitere
 * Einträge (z. B. Mobil, zweite E-Mail) zu verlieren – speichereKanaele ersetzt alle Kanäle.
 */
export function kanaeleMitPrimaer(kanaele: readonly KontaktKanal[], email: string, telefon: string): KontaktKanal[] {
  const setze = (liste: KontaktKanal[], kanal: 'email' | 'telefon', wert: string): KontaktKanal[] => {
    const w = wert.trim();
    const i = liste.findIndex(k => k.kanal === kanal && k.ist_primaer);
    const j = i >= 0 ? i : liste.findIndex(k => k.kanal === kanal);
    if (j >= 0) {
      if (!w) return liste.filter((_, x) => x !== j);
      return liste.map((k, x) => (x === j ? { ...k, wert: w, ist_primaer: true } : k));
    }
    return w ? [...liste, { kanal, wert: w, label: null, ist_primaer: true, sortierung: 0 }] : liste;
  };
  return setze(setze([...kanaele], 'email', email), 'telefon', telefon);
}

/** Ohne Einträge in kontakt_kanaele: Kanäle aus den Spalten bilden (Mobil behält sein Label). */
export function kanaeleAusSpalten(m: Record<string, unknown>): KontaktKanal[] {
  const k: KontaktKanal[] = [];
  if (txt(m.email)) k.push({ kanal: 'email', wert: txt(m.email), label: null, ist_primaer: true, sortierung: 0 });
  if (txt(m.telefon)) k.push({ kanal: 'telefon', wert: txt(m.telefon), label: null, ist_primaer: true, sortierung: 0 });
  if (txt(m.mobil) && txt(m.mobil) !== txt(m.telefon)) {
    k.push({ kanal: 'telefon', wert: txt(m.mobil), label: 'Mobil', ist_primaer: !txt(m.telefon), sortierung: k.filter(x => x.kanal === 'telefon').length });
  }
  return k;
}

const txt = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

// ── #520: weitere Kontaktwege (alles außer der Haupt-E-Mail/-Telefonnummer) ─────────────────

/** Weitere Kontaktwege = alle Einträge, die nicht Haupt-E-Mail bzw. Haupt-Telefon sind. */
export function weitereKanaele(kanaele: readonly KontaktKanal[]): KontaktKanal[] {
  return kanaele.filter(k => !k.ist_primaer);
}

/** Hauptwerte behalten, weitere Kontaktwege ersetzen (nie primär). */
export function mitWeiterenKanaelen(kanaele: readonly KontaktKanal[], weitere: readonly KontaktKanal[]): KontaktKanal[] {
  return [...kanaele.filter(k => k.ist_primaer), ...weitere.map(k => ({ ...k, ist_primaer: false }))];
}

/** Vergleich für „ungespeicherte Änderungen“ (Reihenfolge, Wert, Label, primär). */
export function kanaeleGleich(a: readonly KontaktKanal[] | null, b: readonly KontaktKanal[] | null): boolean {
  const s = (l: readonly KontaktKanal[] | null) => JSON.stringify((l ?? []).filter(k => k.wert.trim()).map(k => [k.kanal, k.wert.trim(), k.label ?? '', k.ist_primaer]));
  return s(a) === s(b);
}
